import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API, authFetch } from "@/App";
import { Send, Loader2, Bot, User, Sparkles } from "lucide-react";

const AVAILABLE_AGENTS = [
  { key: "onboarding", name: "Jasmine", role: "Onboarding Specialist", emoji: "🌟", color: "bg-amber-100 text-amber-700" },
  { key: "conversion", name: "Marcus", role: "Growth Strategist", emoji: "📈", color: "bg-blue-100 text-blue-700" },
  { key: "engagement", name: "Tanya", role: "Community Manager", emoji: "🔥", color: "bg-red-100 text-red-700" },
  { key: "networking", name: "Derek", role: "Networking Coordinator", emoji: "🤝", color: "bg-green-100 text-green-700" },
  { key: "miles", name: "Miles", role: "Community Engagement", emoji: "🍰", color: "bg-purple-100 text-purple-700" },
  { key: "opportunity", name: "Simone", role: "Opportunities Director", emoji: "💼", color: "bg-indigo-100 text-indigo-700" },
];

export const AgentChatDialog = ({ open, onOpenChange }) => {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const selectAgent = (agent) => {
    setSelectedAgent(agent);
    setMessages([{
      role: "agent",
      content: `Hey! I'm ${agent.name}, your ${agent.role}. How can I help you today? Ask me anything about the platform, your clients, or business strategies!`,
      agent: agent
    }]);
  };

  const sendMessage = async () => {
    if (!input.trim() || !selectedAgent || sending) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setSending(true);

    try {
      const res = await authFetch(`${API}/agents/chat`, {
        method: "POST",
        body: JSON.stringify({ agent_key: selectedAgent.key, message: userMsg })
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, {
          role: "agent",
          content: data.response,
          agent: { ...selectedAgent, picture: data.agent?.picture }
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: "agent",
          content: "Sorry, I'm having a bit of trouble right now. Try again in a moment!",
          agent: selectedAgent
        }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: "agent",
        content: "Connection issue — please try again.",
        agent: selectedAgent
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleBack = () => {
    setSelectedAgent(null);
    setMessages([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col p-0 gap-0" data-testid="agent-chat-dialog">
        <DialogHeader className="p-4 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Bot className="w-5 h-5 text-emerald-600" />
            {selectedAgent ? (
              <div className="flex items-center gap-2">
                <span>{selectedAgent.emoji} Chat with {selectedAgent.name}</span>
                <Badge variant="outline" className="text-xs">{selectedAgent.role}</Badge>
              </div>
            ) : (
              "Talk to an Agent"
            )}
          </DialogTitle>
        </DialogHeader>

        {!selectedAgent ? (
          /* Agent Selection */
          <div className="p-4 space-y-3 overflow-y-auto">
            <p className="text-sm text-neutral-500">
              Choose an agent to chat with. Each agent specializes in different aspects of the platform.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {AVAILABLE_AGENTS.map((agent) => (
                <button
                  key={agent.key}
                  onClick={() => selectAgent(agent)}
                  className="p-4 rounded-xl border-2 border-neutral-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all text-left"
                  data-testid={`agent-select-${agent.key}`}
                >
                  <div className="text-2xl mb-2">{agent.emoji}</div>
                  <p className="font-semibold text-sm text-neutral-900">{agent.name}</p>
                  <p className="text-xs text-neutral-500">{agent.role}</p>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Chat Interface */
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px] max-h-[50vh]">
              {messages.map((msg, i) => (
                <div key={i} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  {msg.role === "agent" && (
                    <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 text-sm">
                      {selectedAgent.emoji}
                    </div>
                  )}
                  <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                    msg.role === "user"
                      ? "bg-emerald-600 text-white"
                      : "bg-neutral-100 text-neutral-800"
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  {msg.role === "user" && (
                    <div className="w-7 h-7 rounded-full bg-neutral-200 flex items-center justify-center flex-shrink-0">
                      <User className="w-3.5 h-3.5 text-neutral-600" />
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex gap-2 justify-start">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 text-sm">
                    {selectedAgent.emoji}
                  </div>
                  <div className="bg-neutral-100 rounded-xl px-3 py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            <div className="border-t p-3">
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={handleBack} className="text-neutral-500 text-xs">
                  Switch Agent
                </Button>
                <div className="flex-1 flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    placeholder={`Ask ${selectedAgent.name} anything...`}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    disabled={sending}
                    data-testid="agent-chat-input"
                  />
                  <Button
                    size="sm"
                    onClick={sendMessage}
                    disabled={!input.trim() || sending}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="agent-chat-send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
